import React from 'react';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import IssueIcon from '@mui/icons-material/ReportProblemOutlined';
import SecurityIcon from '@mui/icons-material/SecurityOutlined';
import CoverageIcon from '@mui/icons-material/PercentOutlined';
import DupIcon from '@mui/icons-material/DifferenceOutlined';
import LoadingIcon from '@mui/material/CircularProgress';
import { SonarCloudComponentMeasure } from '@internal/backstage-plugin-bidone-backend';
import { useSonarQubeProjectLinks } from '../../hooks/useSonarQubeProjectLinks';

interface Props {
  metrics: SonarCloudComponentMeasure[];
  projectKey: string;
  isLoading?: boolean;
}

const ValueLink = styled(Link)({
  overflow: 'hidden',
  lineHeight: '24px',
  wordBreak: 'break-word',
  cursor: 'pointer',
});

const findMetricValue = (
  metrics: SonarCloudComponentMeasure[],
  name: string[],
  suffix: string = '',
): string | undefined => {
  for (const n of name) {
    const foundMetric = metrics.find(m => m.metric === n);
    if (foundMetric) {
      // When looking at new code, the response will be in the `periods` array
      // otherwise it will be in the `value` field
      // See https://sonarcloud.io/web_api/api/measures for more details
      const value = foundMetric?.periods?.[0]?.value ?? foundMetric.value;

      // If the metric is a percentage, format it to 1 decimal point
      if (suffix === '%') {
        const floatValue = parseFloat(value);
        if (!isNaN(floatValue)) {
          // Show percentage with 1 decimal point
          return `${floatValue.toFixed(1)}${suffix}`;
        }
      }
      return `${value}${suffix}`;
    }
  }
  return undefined;
};

interface MetricValueProps {
  isloading?: boolean;
  metricValue: string;
  metricUrl: string;
}

const MetricValue = ({
  isloading,
  metricValue,
  metricUrl,
}: MetricValueProps) => {
  if (isloading) {
    return <LoadingIcon size={16} />;
  }
  return (
    <>
      {metricValue && (
        <ValueLink href={metricUrl} target="_blank" rel="noopener">
          {metricValue}
        </ValueLink>
      )}
      {!metricValue && <span>N/A</span>}
    </>
  );
};

const renderMetric = (
  title: string,
  icon: JSX.Element,
  metric: string,
  metrics: SonarCloudComponentMeasure[],
  metricUrl: string,
  isLoading?: boolean,
  suffix: string = '',
) => {
  const metricValue = findMetricValue(metrics, [metric], suffix);
  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={1}>
      <Tooltip arrow title={title} placement="top">
        {icon}
      </Tooltip>
      <MetricValue
        isloading={isLoading}
        metricValue={metricValue || ''}
        metricUrl={metricUrl}
      />
    </Box>
  );
};

export const SonarQubeMetricsGrid = ({
  metrics,
  projectKey,
  isLoading,
}: Props) => {
  // New metrics start with 'new_' prefix, by design by SonarQube
  const newCode = metrics.some(m => m.metric.startsWith('new_'));

  const { getProjectMetricsUrl, projectIssueUrl } =
    useSonarQubeProjectLinks(projectKey);

  return (
    <Grid container spacing={0}>
      <Grid item lg={3} xs={6}>
        {renderMetric(
          'Open Issues',
          <IssueIcon fontSize="large" htmlColor="#d40000ff" />,
          newCode ? 'new_violations' : 'violations',
          metrics,
          `${projectIssueUrl}${newCode ? '&sinceLeakPeriod=true' : ''}`,
          isLoading,
        )}
      </Grid>
      <Grid item lg={3} xs={6}>
        {renderMetric(
          'Security Hotspots',
          <SecurityIcon fontSize="large" htmlColor="#0042bdff" />,
          newCode ? 'new_security_hotspots' : 'security_hotspots',
          metrics,
          getProjectMetricsUrl(
            newCode ? 'new_security_hotspots' : 'security_hotspots',
          ),
          isLoading,
        )}
      </Grid>
      <Grid item lg={3} xs={6}>
        {renderMetric(
          'Code Coverage',
          <CoverageIcon fontSize="large" htmlColor="#007523ff" />,
          newCode ? 'new_coverage' : 'coverage',
          metrics,
          getProjectMetricsUrl(newCode ? 'new_coverage' : 'coverage'),
          isLoading,
          '%',
        )}
      </Grid>
      <Grid item lg={3} xs={6}>
        {renderMetric(
          'Code Duplication',
          <DupIcon fontSize="large" />,
          newCode ? 'new_duplicated_lines_density' : 'duplicated_lines_density',
          metrics,
          getProjectMetricsUrl(
            newCode
              ? 'new_duplicated_lines_density'
              : 'duplicated_lines_density',
          ),
          isLoading,
          '%',
        )}
      </Grid>
    </Grid>
  );
};
